import { useState, type ReactNode } from 'react';
import { ApiError, downloadTaskExport, fetchTaskSummary, fetchTasks } from '../api/client';
import { useSession } from '../app/WorkforceContext';
import { useAsync } from '../app/useAsync';
import {
  EXCLUDED_FROM_EXPORT,
  EXPORT_COLUMNS,
  taskExportPath,
  type TaskFilter,
} from '../lib/dashboard-filters';
import { hasAnyPermission } from '../lib/navigation';
import { FIELD_TASK_STATUSES, formatTaskStatus } from '../lib/task-actions';
import { ErrorCard, LoadingState } from '../states';

/**
 * Dashboard tugas + ekspor CSV.
 *
 * Ekspor memakai `taskExportPath(filter)` — objek filter YANG SAMA dengan yang memuat daftar.
 * Itu yang mencegah regresi klasik "tombol ekspor mengabaikan filter": manajer melihat 12 baris
 * lalu mengunduh berkas berisi seluruh organisasi, dan mengirimkannya lewat surel karena ia
 * mengira isinya sama dengan yang di layar.
 */
export function TaskDashboardPage(): ReactNode {
  const session = useSession();
  const [filter, setFilter] = useState<TaskFilter>({ status: '' });
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<ApiError | null>(null);

  const { state, reload } = useAsync(async () => {
    const [summary, tasks] = await Promise.all([
      fetchTaskSummary(session.auth),
      fetchTasks(session.auth, filter.status || undefined),
    ]);
    return { summary, tasks };
  }, [session.auth, filter.status]);

  const canExport = hasAnyPermission(session.me.permissions, ['workforce.task.export']);

  async function exportCsv(): Promise<void> {
    setExporting(true);
    setExportError(null);
    try {
      const suffix = filter.status ? `-${filter.status}` : '';
      await downloadTaskExport(session.auth, taskExportPath(filter), `field-tasks${suffix}.csv`);
    } catch (caught: unknown) {
      setExportError(
        caught instanceof ApiError
          ? caught
          : new ApiError(0, 'UNKNOWN', 'Kesalahan tidak dikenal.', null),
      );
    } finally {
      setExporting(false);
    }
  }

  return (
    <>
      <section className="card">
        <h2>Pantauan tugas</h2>
        {/*
          Saringan Status DIPINDAH ke kartu Ekspor (22 Agu 2026), tempatnya bekerja.

          Dulu ia berdiri di sini, di atas papan Ringkasan — dan papan itu tidak pernah bergerak
          olehnya: `fetchTaskSummary` tidak menerima filter sama sekali. Satu-satunya yang berubah
          adalah angka "N baris" di dalam kartu Ekspor, jauh di bawah layar. Bagi penyelia tanpa
          izin ekspor, kartu itu bahkan tidak dirender, sehingga saringan ini tidak mengubah APA PUN
          yang terlihat.

          Kontrol yang tampak mengendalikan sesuatu lalu tidak melakukannya membuat orang
          menyimpulkan aplikasinya rusak — dan pada hari pertama, kesimpulan itu mahal.
        */}
        <p className="muted small">
          Angka di bawah mencakup seluruh tugas organisasi, bukan hanya tugas Anda.
        </p>
      </section>

      {state.status === 'loading' ? <LoadingState label="Memuat ringkasan…" /> : null}
      {state.status === 'error' ? <ErrorCard error={state.error} onRetry={reload} /> : null}

      {state.status === 'ready' ? (
        <>
          <section className="card">
            <h3>Ringkasan</h3>
            <div className="board">
              {FIELD_TASK_STATUSES.map((s) => (
                <div key={s} className="board-cell">
                  <span className="board-count">{state.data.summary[s] ?? 0}</span>
                  <span className="muted small">{formatTaskStatus(s)}</span>
                </div>
              ))}
            </div>
            {/*
              Kalimat untuk keadaan HARI PERTAMA — saat setiap angkanya nol.

              Deretan nol memang jujur, tetapi ia tidak membedakan "belum ada tugas" dari "papannya
              gagal memuat", dan itulah pertanyaan pertama penyelia yang baru memakai aplikasi ini.
              Papan Absensi sudah menjelaskan keadaan kosongnya ("Belum ada pekerja aktif…"); papan
              ini tertinggal, dan ketimpangan itu yang ditutup di sini.

              TIDAK menyebut saringan: `fetchTaskSummary` tidak menerima filter, jadi angka-angka
              ini SELALU mencakup seluruh organisasi. Semua nol berarti memang tidak ada tugas —
              menyalahkan saringan di sini akan menuduh hal yang tidak melakukannya.
            */}
            {FIELD_TASK_STATUSES.every((s) => (state.data.summary[s] ?? 0) === 0) ? (
              <p className="muted small">
                Belum ada tugas sama sekali. Papan ini sudah termuat — angkanya memang nol.
              </p>
            ) : null}
          </section>

          {canExport ? (
            <section className="card">
              <h3>Ekspor CSV</h3>
              {/*
                Saringan berdiri DI DALAM kartu ini karena hanya isi kartu ini yang berubah
                olehnya — jumlah baris tepat di bawahnya, dan berkas yang diunduh.
              */}
              <label htmlFor="status">Status</label>
              <select
                id="status"
                value={filter.status}
                onChange={(e) => setFilter({ status: e.target.value })}
              >
                <option value="">Semua</option>
                {FIELD_TASK_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {formatTaskStatus(s)}
                  </option>
                ))}
              </select>
              <p className="muted small">
                Berkas mengikuti saringan di atas
                {filter.status ? ` (${formatTaskStatus(filter.status)})` : ' (semua status)'} —{' '}
                {state.data.tasks.length} baris.
              </p>
              {/*
                Menyatakan isi berkas dengan jujur. Kontak, telepon, dan koordinat tujuan
                SENGAJA tidak ikut (ADR-0036 D5): ekspor berpindah tangan, dan data yang tidak
                dibutuhkan laporan operasional tidak ikut keluar. UI tidak boleh menjanjikan
                kolom yang memang tidak diekspor.
              */}
              <p className="muted small">{EXPORT_COLUMNS.length} kolom.</p>
              <p className="muted small">
                Tidak termasuk: {EXCLUDED_FROM_EXPORT.join(', ')} — sengaja tidak diekspor.
              </p>
              <button
                type="button"
                className="button-primary"
                disabled={exporting}
                onClick={() => void exportCsv()}
              >
                {exporting ? 'Menyiapkan berkas…' : 'Unduh CSV'}
              </button>
              <p className="muted small">Setiap unduhan tercatat di jejak audit.</p>
            </section>
          ) : null}

          {exportError ? <ErrorCard error={exportError} /> : null}
        </>
      ) : null}
    </>
  );
}
